import React, { useEffect } from 'react';
import styled from 'styled-components';
import LightButton from '~/editor/buttons/LightButton';
import { UploadImageIcon, UploadVideoIcon, UploadPdfIcon } from '../icons';
import { useFileUpload } from '../hooks/useFileUpload';
import MediaGrid from './MediaGrid';
import { MediaFile } from './MediaGrid';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';

const Container = styled.div`
  h3 {
    font-family: ralewayBold;
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    padding-bottom: 10px;
    font-weight: unset !important;
  }
`;

const UploadArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #f8f1ff;
  border-radius: 10px;
  padding: 16px;
  border: 1px dashed #9738ff;
  height: 100px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
  user-select: none;
  /* -webkit-user-drag: none; */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  button {
    background: transparent;
    border: 2px solid #9738ff;
    color: #9738ff;
    border-radius: 8px;
    height: 38px;
    cursor: pointer;
    pointer-events: auto;
  }

  .text {
    flex: 1;
    padding-left: 10px;
    pointer-events: none;

    b {
      font-family: ralewayBold;
      font-weight: unset !important;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0%;
      color: #000000;
      pointer-events: none;
    }

    p {
      padding-left: 0px;
      font-family: Raleway;
      font-weight: 400;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 0%;
      color: #6f6f6f;
      pointer-events: none;
    }
  }

  .icon {
    pointer-events: none;
  }
`;

type UploadSectionProps = {
  type: 'image' | 'video' | 'document' | 'audio';
  files: MediaFile[];
  loading: boolean;
  selectedId?: number;
  onSelect: (file: MediaFile) => void;
  onDelete: (id: number) => void;
  onFileUpload: (file: MediaFile) => void;
  loadMoreRef: React.RefObject<HTMLDivElement>;
  onUploadingChange?: (uploading: boolean, cancelUpload?: () => void) => void;
};

const UploadSection: React.FC<UploadSectionProps> = ({
  type,
  files,
  loading,
  selectedId,
  onSelect,
  onDelete,
  onFileUpload,
  loadMoreRef,
  onUploadingChange
}) => {
  const { uploadFile, uploading, uploadText, cancelUpload } =
    useFileUpload(type);

  useEffect(() => {
    onUploadingChange?.(uploading, cancelUpload);
  }, [uploading, onUploadingChange]);

  // Warn user before closing tab during file upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uploading) {
        e.preventDefault();
        // included for legacy support - custom messages deprecated since 2016
        e.returnValue = true;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploading]);

  const getAcceptType = () => {
    switch (type) {
      case 'image':
        return 'image/png, image/jpeg';
      case 'video':
        return 'video/mp4';
      case 'audio':
        return 'audio/mpeg, audio/wav';
      case 'document':
        return 'application/pdf, application/x-pdf, application/octet-stream';
      default:
        return '';
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const result = await uploadFile(file);
      if (result) {
        onFileUpload(result);
      }
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files[0];
    if (file) {
      const validTypes = getAcceptType()
        .split(', ')
        .map(type => type.trim());
      const isValidType = validTypes.includes(file.type);

      if (!isValidType) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Invalid file type'
        });
        return;
      }

      const result = await uploadFile(file);
      if (result) {
        onFileUpload(result);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // event.currentTarget.style.borderColor = '#9738ff';
    // event.currentTarget.style.backgroundColor = '#f0e5ff';
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // event.currentTarget.style.borderColor = '#9738ff';
    // event.currentTarget.style.backgroundColor = '#f8f1ff';
  };

  const getUploadIcon = () => {
    switch (type) {
      case 'document':
        return <UploadPdfIcon />;
      case 'image':
        return <UploadImageIcon />;
      case 'video':
        return <UploadVideoIcon />;
      case 'audio':
        return <UploadVideoIcon />;
    }
  };

  const getTypeText = () => {
    switch (type) {
      case 'document':
        return 'Document';
      case 'image':
        return 'Image';
      case 'video':
        return 'Video';
      case 'audio':
        return 'Audio';
      default:
        return 'Unknown';
    }
  };

  const getSizeLimit = () => {
    switch (type) {
      case 'document':
        return '50 MB';
      case 'image':
        return '16 MB';
      case 'video':
        return '1 GB';
      case 'audio':
        return '10 MB';
      default:
        return 'Unknown Size';
    }
  };

  const getFileFormat = () => {
    switch (type) {
      case 'document':
        return 'PDF';
      case 'image':
        return 'JPEG and PNG';
      case 'video':
        return 'MP4';
      case 'audio':
        return 'MP3 and WAV';
      default:
        return 'Unknown';
    }
  };

  return (
    <Container>
      <h3>File Upload</h3>
      <UploadArea
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        data-accept="*/*"
      >
        <div className="icon">{getUploadIcon()}</div>
        <div className="text">
          <b>Drop Your {getTypeText()} Here</b>
          <p>
            Upload files up to {getSizeLimit()}
            {type != 'document' && (
              <span>, resolution should be larger than 1000x1000 px.</span>
            )}
            <br />
            Accepted file formats: {getFileFormat()}
          </p>
        </div>
        <LightButton style={{ width: '155px' }}>
          <input
            type="file"
            accept={getAcceptType()}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="fileInput"
            disabled={uploading}
          />
          <label
            htmlFor="fileInput"
            style={{ cursor: 'pointer', display: 'flex' }}
          >
            {uploading ? uploadText : 'Browse file'}
          </label>
        </LightButton>
      </UploadArea>

      {files.length > 0 && <h3>Recently uploaded</h3>}
      <MediaGrid
        files={files}
        loading={loading}
        type={type}
        variant="small"
        selectedId={selectedId}
        onSelect={onSelect}
        onDelete={onDelete}
        loadMoreRef={loadMoreRef}
        showNoResults
      />
    </Container>
  );
};

export default UploadSection;
